import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  PLATFORM_ID,
  inject,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { register } from 'swiper/element/bundle';

if (typeof window !== 'undefined' && !customElements.get('swiper-container')) {
  register();
}

@Component({
  selector: 'app-amenities',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './amenities.html',
  styleUrl: './amenities.css'
})
export class AmenitiesComponent implements AfterViewInit, OnDestroy {
  @ViewChild('swiperEl') swiperEl!: ElementRef;
  @ViewChild('lgContainer') lgContainer!: ElementRef;

  private platformId = inject(PLATFORM_ID);
  private lgInstance: any = null;
  private lgSetupPromise: Promise<void> | null = null;

  amenities = [
    { icon: 'restaurant', name: 'Gourmet Restaurant', desc: 'A celebration of taste and atmosphere, bringing the elegance of lodge dining to the city skyline.' },
    { icon: 'pool', name: 'Horizon Pool', desc: 'Suspended 23 floors above the city, the infinity pool blurs the boundary between earth and sky.' },
    { icon: 'spa', name: 'Wellness Spa', desc: 'A retreat for body and mind combining restorative treatments, serene design, and panoramic views.' },
    { icon: 'garden', name: 'Sky Garden', desc: 'A lush rooftop sanctuary where greenery meets open sky, designed for reflection and calm.' },
    { icon: 'gym', name: 'Fitness Gym', desc: 'Modern training equipment paired with a yoga pavilion and sweeping city views for inspired wellness.' },
    { icon: 'retail', name: 'Retail Gallery', desc: 'Curated boutiques, cafés, and lifestyle destinations that bring sophistication to street level.' },
    { icon: 'play', name: 'Play Haven', desc: 'A nature-inspired children\'s haven where young imaginations thrive in a beautifully crafted setting.' },
    { icon: 'lounge', name: 'Residents\' Lounge', desc: 'An exclusive gathering space for quiet moments, connections, and the art of unhurried living.' },
  ];

  readonly slides = [
    { src: '/exterior/new/entrance1.webp',         category: 'Arrival',            label: 'The Entrance' },
    { src: '/amenities/new/reception16.webp',      category: '', label: 'Reception' },
    { src: '/amenities/new/restaurant4.webp',      category: 'Gourmet Restaurant', label: 'Signature Dining' },
    { src: '/amenities/new/spa4.webp',             category: 'Wellness Spa',       label: 'Spa Sanctuary' },
    { src: '/amenities/new/rooftopgarden7.webp',   category: 'Sky Garden',         label: 'Sky Garden' },
    { src: '/amenities/new/gym2.webp',             category: 'Fitness Gym',        label: 'Training Floor' },
    { src: '/amenities/new/cafe4.webp',            category: 'Retail Gallery',     label: 'The Café' },
    { src: '/amenities/new/kidplayarea7.webp',     category: 'Play Haven',         label: 'Children\'s Play Area' },
    { src: '/amenities/new/residencelounge1.webp', category: 'Residents\' Lounge', label: 'Residence Lounge' },
    { src: '/amenities/new/gameroom2.webp',        category: 'Game Room',          label: 'Game Lounge' },
    { src: '/amenities/new/flowershop6.webp',      category: 'Flower Shop',        label: 'Botanical Corner' },
  ];

  readonly pagination = { clickable: true };
  readonly autoplay = { delay: 3600, disableOnInteraction: false, pauseOnMouseEnter: true };
  readonly breakpoints = { 481: { slidesPerView: 1.12, spaceBetween: 20 } };

  prevSlide() { this.swiperEl.nativeElement.swiper.slidePrev(); }
  nextSlide() { this.swiperEl.nativeElement.swiper.slideNext(); }

  async ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    await this.ensureLightGallery();
  }

  async openAt(index: number, event: Event) {
    event.stopPropagation();
    await this.ensureLightGallery();
    this.lgInstance?.openGallery(index);
  }

  ngOnDestroy() {
    this.lgInstance?.destroy();
  }

  private ensureLightGallery(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.lgInstance) return Promise.resolve();
    if (!this.lgSetupPromise) {
      this.lgSetupPromise = Promise.all([
        import('lightgallery'),
        import('lightgallery/plugins/zoom'),
        import('lightgallery/plugins/fullscreen')
      ]).then(([{ default: lightGallery }, { default: lgZoom }, { default: lgFullscreen }]) => {
        this.lgInstance = lightGallery(this.lgContainer.nativeElement, {
          plugins: [lgZoom, lgFullscreen],
          dynamic: true,
          dynamicEl: this.slides.map(s => ({ src: s.src, subHtml: `<h4>${s.category}</h4><p>${s.label}</p>` })),
          speed: 500, zoom: true, download: false, counter: true,
        });
      });
    }
    return this.lgSetupPromise;
  }
}
